/**
 * «Создать вариант плана» — единственный вход в заведение варианта.
 *
 * История (hotfix, founder UAT 19.08): ручка `POST /planning/scenarios` жила
 * с #803, а кнопки к ней в интерфейсе не было НИГДЕ — посевной план, СПП и
 * сводка требовали вариант, честно писали «варианта нет», и на этом флоу
 * заканчивался тупиком. Классика «ручка есть — входа нет».
 *
 * Компонент ОДИН на все экраны планирования (посевной план — пустое состояние
 * и шапка у переключателя вариантов; СПП — пустое состояние): копия кнопки в
 * каждом экране разъехалась бы текстами и гейтом прав при первой же правке.
 *
 * Право — `season_plan:U` («вести бюджет сезона», как открытие бюджета в
 * хабе). Без права в пустом состоянии — честный текст, а не кнопка с
 * гарантированным 403; в шапке — просто ничего.
 *
 * Имена вариантов уникальны внутри хозяйство×сезон (решение founder 21.08).
 * Правило то же самое, что у переименования — общий модуль
 * `lib/planning/variantName`: разъедься они, и запрет обходился бы этой самой
 * кнопкой.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { describeApiError } from '@/lib/apiError';
import { useCan } from '@/lib/auth/usePermissions';
import { useCreatePlanVariant } from '@/lib/planning/techCards';
import { VARIANT_NAME_MAX, variantNameError } from '@/lib/planning/variantName';

/**
 * Название по умолчанию: «Вариант N» — первое свободное число мимо занятых.
 * Считаем по названиям, а не по количеству: после «Вариант 1» + «Интенсив»
 * следующий — «Вариант 2», а не «Вариант 3».
 */
export function nextVariantName(existing: readonly string[]): string {
  const taken = new Set(existing.map((n) => n.trim().toLowerCase()));
  let n = 1;
  while (taken.has(`вариант ${n}`)) n += 1;
  return `Вариант ${n}`;
}

export function CreateVariantButton({
  farmId,
  seasonId,
  seasonYear,
  farmName,
  existingNames,
  onCreated,
  emptyState = false,
}: {
  farmId: string | null;
  seasonId: string | null;
  seasonYear: number;
  farmName?: string | null;
  /** Названия уже заведённых вариантов — для «Вариант N» по умолчанию. */
  existingNames: readonly string[];
  /** Вызывается после создания — экран выбирает новый вариант в адресе. */
  onCreated: (variantId: string) => void;
  /** true — большая кнопка пустого состояния; false — «+ Новый вариант» в шапке. */
  emptyState?: boolean;
}) {
  const canCreate = useCan('season_plan', 'u');
  const create = useCreatePlanVariant();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  /** Отказ сервера (409 при гонке, 403, сеть) — рядом с полем, не тостом в никуда. */
  const [serverError, setServerError] = useState<string | null>(null);

  if (!canCreate) {
    // Fail-closed (`useCan`): и «права нет», и «/me ещё грузится» — без кнопки.
    return emptyState ? (
      <p className="text-sm text-muted-foreground">
        Заводить варианты плана может только пользователь с правом ведения бюджета
        сезона — обратитесь к экономисту или администратору.
      </p>
    ) : null;
  }

  const openDialog = () => {
    setName(nextVariantName(existingNames));
    setServerError(null);
    setOpen(true);
  };

  const nameError = variantNameError(name, existingNames);

  const submit = () => {
    if (!farmId || !seasonId || nameError || create.isPending) return;
    setServerError(null);
    create.mutate(
      { name: name.trim(), farmId, seasonId },
      {
        onSuccess: (created) => {
          setOpen(false);
          onCreated(created.id);
        },
        // Диалог не закрываем: введённое имя остаётся в поле, человек правит
        // его, а не набирает заново.
        onError: (err) => setServerError(describeApiError(err)),
      },
    );
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={emptyState ? 'default' : 'outline'}
        disabled={!farmId || !seasonId}
        onClick={openDialog}
      >
        {emptyState ? 'Создать вариант плана' : '+ Новый вариант'}
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Новый вариант плана</DialogTitle>
            <DialogDescription>
              {farmName ? `Хозяйство «${farmName}»` : 'Выбранное хозяйство'}, сезон{' '}
              {seasonYear}. Вариант заводится пустым: дальше — распределение культур по
              полям и назначение техкарт. Вариантов может быть несколько, план сезона —
              тот, который выберут для утверждения.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="create-variant-name">Название варианта</Label>
              <Input
                id="create-variant-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setServerError(null);
                }}
                autoFocus
                maxLength={VARIANT_NAME_MAX}
                aria-invalid={!!nameError || !!serverError}
              />
              {nameError && <p className="text-sm text-destructive">{nameError}</p>}
              {!nameError && serverError && (
                <p className="text-sm text-destructive">{serverError}</p>
              )}
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={!!nameError || create.isPending}>
                {create.isPending ? 'Заводим…' : 'Создать вариант'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
